// Shared A2P formatting + reference data.
//
// Extracted from the inline helpers in src/components/settings/A2PRegistration.jsx
// so the Settings wizard AND the onboarding A2P capture step
// (src/components/onboarding/A2PCaptureStep.jsx) use ONE source of truth. Twilio
// is strict about field shapes (EIN digits-only, phone E.164, vertical/state from
// fixed sets) and a malformed submit costs days of carrier re-review, so keep all
// normalization here.

// EIN/SSN → digits only. Twilio's business_registration_number wants no dashes.
export const formatEINForTwilio = (ein) => {
  if (!ein) return '';
  return ein.replace(/\D/g, '');
};

// Pretty EIN/SSN for display only (converted back via formatEINForTwilio before send).
export const formatEINForDisplay = (ein, businessType) => {
  if (!ein) return '';
  const numbersOnly = ein.replace(/\D/g, '');

  // SSN format XXX-XX-XXXX for sole proprietors.
  if (businessType === 'SOLE_PROPRIETORSHIP') {
    if (numbersOnly.length > 5) {
      return `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3, 5)}-${numbersOnly.slice(5, 9)}`;
    } else if (numbersOnly.length > 3) {
      return `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3, 5)}`;
    }
    return numbersOnly;
  }

  // EIN format XX-XXXXXXX otherwise.
  if (numbersOnly.length > 2) {
    return `${numbersOnly.slice(0, 2)}-${numbersOnly.slice(2, 9)}`;
  }
  return numbersOnly;
};

// Phone → E.164 (+1XXXXXXXXXX) for US numbers.
export const formatPhoneForTwilio = (phone) => {
  if (!phone) return '';
  const numbersOnly = phone.replace(/\D/g, '');
  if (numbersOnly.length === 10) {
    return `+1${numbersOnly}`;
  }
  if (numbersOnly.length === 11 && numbersOnly.startsWith('1')) {
    return `+${numbersOnly}`;
  }
  if (!phone.startsWith('+')) {
    return `+${numbersOnly}`;
  }
  return phone;
};

// Twilio business-type values (mirrors the wizard dropdown).
export const BUSINESS_TYPES = [
  { value: 'LLC', label: 'LLC' },
  { value: 'CORPORATION', label: 'Corporation' },
  { value: 'SOLE_PROPRIETORSHIP', label: 'Sole Proprietorship' },
  { value: 'NON_PROFIT', label: 'Non-Profit' },
];

// Twilio A2P verticals (mirrors the wizard's business_industry dropdown).
export const TWILIO_VERTICALS = [
  'AGRICULTURE', 'AUTOMOTIVE', 'BANKING', 'CONSTRUCTION', 'CONSUMER', 'EDUCATION',
  'ELECTRONICS', 'ENGINEERING', 'ENERGY', 'FAST_MOVING_CONSUMER_GOODS',
  'FINANCIAL', 'FINTECH', 'FOOD_AND_BEVERAGE', 'GOVERNMENT', 'HEALTHCARE',
  'HOSPITALITY', 'INSURANCE', 'JEWELRY', 'LEGAL', 'MANUFACTURING', 'MEDIA',
  'NOT_FOR_PROFIT', 'OIL_AND_GAS', 'ONLINE', 'PROFESSIONAL_SERVICES', 'RAW_MATERIALS',
  'REAL_ESTATE', 'RELIGION', 'RETAIL', 'TECHNOLOGY', 'TELECOMMUNICATIONS',
  'TRANSPORTATION', 'TRAVEL',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }));

// A2P campaign opt-in / consent descriptions (Twilio's MessageFlow field).
// Mirrors the Settings wizard's presets. `flow` is the exact string submitted to
// Twilio; we store it on the customer profile at onboarding so the campaign can
// later auto-fire with the tenant's real consent description (carriers scrutinize
// this field, so it must be accurate, not a blanket default).
export const OPT_IN_PRESETS = [
  { value: 'default',  label: 'Web form submission (Recommended)', flow: 'Leads opt in by submitting a contact or inquiry form' },
  { value: 'inbound',  label: 'Inbound text or form submission',   flow: 'Leads opt in by texting the business first or submitting a form' },
  { value: 'existing', label: 'Existing customer contact',         flow: 'Existing customers who gave their number and agreed to be contacted' },
  { value: 'referral', label: 'Referral or event signup',          flow: 'Leads opt in at an event or through a referral signup form' },
];

// US states + DC for the address Region field (Twilio wants the 2-letter code).
export const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM',
  'NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA',
  'WV','WI','WY',
];

// Best-guess map from Surf's free-text industry → a Twilio vertical, used to
// pre-select the dropdown. The tenant can override. Keyword-matched; defaults to
// PROFESSIONAL_SERVICES (the safest generic vertical) when nothing matches.
export const industryToVertical = (industry) => {
  if (!industry || typeof industry !== 'string') return 'PROFESSIONAL_SERVICES';
  const s = industry.toLowerCase();
  const rules = [
    [/real estate|realtor|wholesal|property|housing|mortgage broker/, 'REAL_ESTATE'],
    [/insur/, 'INSURANCE'],
    [/health|medical|dental|clinic|hospital|wellness|therap/, 'HEALTHCARE'],
    [/staffing|recruit|hr |human resources|talent/, 'PROFESSIONAL_SERVICES'],
    [/legal|law|attorney|paralegal/, 'LEGAL'],
    [/bank|lend|loan|credit union/, 'BANKING'],
    [/fintech/, 'FINTECH'],
    [/financ|invest|wealth|accounting|tax/, 'FINANCIAL'],
    [/auto|car dealer|dealership|vehicle/, 'AUTOMOTIVE'],
    [/construct|contractor|roofing|hvac|plumb|remodel/, 'CONSTRUCTION'],
    [/restaurant|food|beverage|catering/, 'FOOD_AND_BEVERAGE'],
    [/hotel|hospitality|resort/, 'HOSPITALITY'],
    [/retail|store|ecommerce|e-commerce|shop/, 'RETAIL'],
    [/saas|software|tech|app|platform/, 'TECHNOLOGY'],
    [/educat|school|tutor|course|training/, 'EDUCATION'],
    [/travel|tour|cruise/, 'TRAVEL'],
    [/energy|solar|utility/, 'ENERGY'],
    [/transport|logistics|trucking|freight/, 'TRANSPORTATION'],
    [/non.?profit|charity|church|religio/, 'NOT_FOR_PROFIT'],
    [/media|marketing|advertis|agency/, 'MEDIA'],
  ];
  for (const [re, vertical] of rules) {
    if (re.test(s)) return vertical;
  }
  return 'PROFESSIONAL_SERVICES';
};
